/**
 * Cuenta atrás hasta la fecha configurada en la sección s-cuenta-atras.
 * La fecha se interpreta siempre en hora de España, sea cual sea la zona del cliente.
 */
const TIME_ZONE = 'Europe/Madrid';

/**
 * Diferencia en ms entre la hora local de `timeZone` y UTC en un instante dado.
 * @param {number} timestamp
 * @param {string} timeZone
 */
function getTimeZoneOffset(timestamp, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(timestamp);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, Number(value)]));
  const localAsUtc = Date.UTC(values.year, values.month - 1, values.day, values.hour, values.minute, values.second);

  return localAsUtc - Math.floor(timestamp / 1000) * 1000;
}

/**
 * Convierte "AAAA-MM-DD HH:MM" (hora opcional) en `timeZone` a un timestamp UTC.
 * @param {string | undefined} value
 * @param {string} timeZone
 * @returns {number | null}
 */
function parseZonedDate(value, timeZone) {
  const match = value?.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (!match) return null;

  const [year, month, day, hour, minute] = match.slice(1).map((part) => Number(part ?? 0));
  if (hour > 23 || minute > 59) return null;

  const localAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const check = new Date(localAsUtc);
  if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;

  // Segunda pasada para acertar el offset en los días de cambio de hora
  const firstGuess = localAsUtc - getTimeZoneOffset(localAsUtc, timeZone);
  return localAsUtc - getTimeZoneOffset(firstGuess, timeZone);
}

class SCuentaAtras extends HTMLElement {
  /** @type {number | null} */
  #target = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;

  connectedCallback() {
    this.#target = parseZonedDate(this.dataset.endDate, TIME_ZONE);

    if (this.#target === null) {
      this.dataset.state = 'invalid';
      return;
    }

    this.#tick();
  }

  disconnectedCallback() {
    clearTimeout(this.#timeout);
  }

  #tick = () => {
    if (this.#target === null) return;

    const remaining = this.#target - Date.now();
    if (remaining <= 0) {
      this.dataset.state = 'ended';
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    this.#setUnit('days', Math.floor(totalSeconds / 86400));
    this.#setUnit('hours', Math.floor((totalSeconds % 86400) / 3600));
    this.#setUnit('minutes', Math.floor((totalSeconds % 3600) / 60));
    this.#setUnit('seconds', totalSeconds % 60);
    this.dataset.state = 'running';

    // Alinea el siguiente tick con el cambio de segundo
    this.#timeout = setTimeout(this.#tick, remaining % 1000 || 1000);
  };

  /**
   * @param {string} unit
   * @param {number} value
   */
  #setUnit(unit, value) {
    const element = this.querySelector(`[data-unit="${unit}"]`);
    const text = String(value).padStart(2, '0');
    if (element && element.textContent !== text) element.textContent = text;
  }
}

if (!customElements.get('s-cuenta-atras')) {
  customElements.define('s-cuenta-atras', SCuentaAtras);
}
